import { getAiValueTrons, tronsToCents } from '@shared/all/domains/products';

/**
 * Currency formatting utilities for consistent price display across platforms
 */

/**
 * Formats a price in cents to USD currency string
 * @param priceInCents - Price as integer in cents (e.g., 1299 for $12.99)
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted USD string (e.g., "$12.99")
 */
export const formatUSD = (priceInCents: number, locale = 'en-US'): string => {
  return formatPrice(priceInCents, 'USD', locale);
};

export const formatPrice = (
  priceInCents: number,
  currency = 'USD',
  locale = 'en-US'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(priceInCents / 100);
};

/**
 * "$10,050.00" — what a tron balance can actually buy in AI.
 *
 * A balance is a fixed number of trons but NOT a fixed amount of AI: a
 * non-premium member spends the margin on top of every call. Goes through
 * `formatUSD` so the thousands separators and decimals match every other
 * price on the platform.
 */
export const formatAiValue = (
  balanceTrons: number,
  isPremium: boolean,
  marginPercent: number,
  locale = 'en-US'
): string =>
  formatUSD(
    tronsToCents(getAiValueTrons(balanceTrons, isPremium, marginPercent)),
    locale
  );

/**
 * Converts store price string to cents integer
 * Handles various store price formats: "$12.99", "12.99", etc.
 * @param storePrice - Price string from app store
 * @returns Price in cents as integer
 */
export const storePriceToCents = (storePrice: string | number): number => {
  if (typeof storePrice === 'number') {
    // Assume it's already in dollars, convert to cents
    return Math.round(storePrice * 100);
  }

  // Remove currency symbols and parse
  const numericValue = parseFloat(storePrice.replace(/[^0-9.-]/g, ''));
  if (isNaN(numericValue)) {
    return 0;
  }

  return Math.round(numericValue * 100);
};

/**
 * For React Native: safely format price from app store
 * @param storePrice - Price from expo-iap (can be string or number)
 * @returns Formatted currency string or null if no store price available
 */
export const formatStorePriceRN = (
  storePrice: string | number | undefined
): string | null => {
  if (storePrice === undefined || storePrice === null) {
    return null; // No store price available
  }

  if (typeof storePrice === 'string') {
    // Store already formatted it (e.g., "$12.99", "€9.99"), return as-is
    return storePrice;
  }

  if (typeof storePrice === 'number') {
    // Store price in dollars, format it properly
    return formatUSD(Math.round(storePrice * 100));
  }

  return null;
};
